import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { adminAPI } from '../services/api';
import { formatTableDate } from '../utils/dateUtils';

import { 
  getTableHeaderStyles, 
  getTableContainerStyles, 
  getTableStyles,
  getTableCellStyles,
  getTableRowStyles
} from '../styles/tableStyles';
import ApiKeyDialog from '../components/ApiKeyDialog';
import { ApiKey } from '../types';

interface Company {
  id: string;
  name: string;
  domain: string;
  status: string;
  lastLoginAt?: string;
  planName?: string;
  planPrice?: number;
  planId?: number;
  planFeatures?: any;
  subscriptionStatus?: string;
}

const ApiKeys: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();

  const [apiKeys, setApiKeys] = React.useState<ApiKey[]>([]);
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showRegenerateDialog, setShowRegenerateDialog] = React.useState(false);
  const [showFullKeyDialog, setShowFullKeyDialog] = React.useState(false);
  const [selectedKey, setSelectedKey] = React.useState<ApiKey | null>(null);
  const [fullApiKey, setFullApiKey] = React.useState<string>('');
  const [loadingFullKey, setLoadingFullKey] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [keysResponse, companiesResponse] = await Promise.all([
        adminAPI.getApiKeys(),
        adminAPI.getCompanies(),
      ]);

      setApiKeys(keysResponse.data || []);
      
      // Backend now filters out the root company, so no need for frontend filtering
      setCompanies(companiesResponse.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!selectedKey) return;
    
    try {
      await adminAPI.regenerateApiKey(selectedKey.id);
      showToast('API key regenerated successfully!', 'success');
      setShowRegenerateDialog(false);
      setSelectedKey(null);
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Failed to regenerate API key', 'error');
    }
  };

  const handleCopyKey = async (keyId: string) => {
    try {
      // Fetch the full API key from the backend
      const response = await adminAPI.getApiKey(keyId);
      const fullKey = response.data.fullToken || response.data.tokenPrefix || '';
      navigator.clipboard.writeText(fullKey);
      showToast('API key copied to clipboard!', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to copy API key', 'error');
    }
  };

  const handleShowFullApiKey = async (key: ApiKey) => {
    try {
      setLoadingFullKey(true);
      setSelectedKey(key);
      setShowFullKeyDialog(true);
      
      // Fetch the API key from the backend
      const response = await adminAPI.getApiKey(key.id);
      setFullApiKey(response.data.fullToken || response.data.tokenPrefix || '');
    } catch (err: any) {
      showToast(err.message || 'Failed to fetch full API key', 'error');
      setShowFullKeyDialog(false);
    } finally {
      setLoadingFullKey(false);
    }
  };

  const getCompanyName = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown Company';
  };

  // Get current page data
  const currentApiKeys = apiKeys.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Safety check for data structure
  if (!Array.isArray(apiKeys)) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Invalid data structure received from server. Please try refreshing the page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
          API Keys
        </Typography>
        

      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* API Keys Table */}
      <Paper sx={getTableContainerStyles()}>
        <TableContainer>
          <Table {...getTableStyles()}>
            <TableHead>
              <TableRow sx={getTableRowStyles()}>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Name
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Company
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  API Key
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Status
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Created
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Last Used
                </TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>
                  Usage
                </TableCell>
                <TableCell align="center" sx={getTableHeaderStyles(isDarkMode)}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={8} align="center" sx={getTableCellStyles()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={8} align="center" sx={getTableCellStyles()}>
                    <Alert severity="error" sx={{ my: 2 }}>
                      {error}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : apiKeys.length === 0 ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={8} align="center" sx={getTableCellStyles()}>
                    <Typography variant="body2" color="text.secondary">
                      No API keys found. Create your first API key to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                currentApiKeys
                  .filter(key => key && key.id && key.tokenPrefix) // Use camelCase
                  .map((key) => (
                  <TableRow key={key.id} sx={getTableRowStyles()}>
                    <TableCell sx={getTableCellStyles()}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {key.name}
                        </Typography>
                        {key.description && (
                          <Typography variant="caption" color="text.secondary">
                            {key.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>{getCompanyName(key.companyId || '')}</TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {`${(key.tokenPrefix || 'sk_...').substring(0, 20)}...`}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleShowFullApiKey(key)}
                          sx={{ p: 0 }}
                        >
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyKey(key.id)}
                          sx={{ p: 0 }}
                        >
                          <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Chip 
                        label={key.isActive ? 'Active' : 'Expired'} 
                        size="small"
                        color={key.isActive ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {formatTableDate(key.createdAt, 'created')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography variant="body2">
                        {formatTableDate(key.lastUsedAt, 'lastUsed')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Chip 
                        label={`${key.usageCount || 0} calls`}
                        size="small"
                        color={(key.usageCount || 0) > 0 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center" sx={getTableCellStyles()}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Regenerate Key">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedKey(key);
                              setShowRegenerateDialog(true);
                            }}
                            color="warning"
                          >
                            <Refresh sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={apiKeys.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Regenerate API Key Dialog */}
      <Dialog open={showRegenerateDialog} onClose={() => setShowRegenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerate API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Warning: Regenerating this API key will invalidate the current key. 
            Make sure to update any applications using this key.
          </Alert>
          <Typography variant="body2">
            Are you sure you want to regenerate the API key for <strong>{selectedKey?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRegenerateDialog(false)}>Cancel</Button>
          <Button onClick={handleRegenerateKey} variant="contained" color="warning">
            Regenerate Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full API Key Dialog */}
      <ApiKeyDialog
        open={showFullKeyDialog}
        onClose={() => setShowFullKeyDialog(false)}
        selectedKey={selectedKey}
        fullApiKey={fullApiKey}
        loadingFullKey={loadingFullKey}
        onCopyKey={(fullKey) => {
          navigator.clipboard.writeText(fullKey);
          showToast('API key copied to clipboard!', 'success');
        }}
        showCompany={true}
        getCompanyName={getCompanyName}
      />
    </Box>
  );
};

export default ApiKeys; 